import { Fragment, PropsWithChildren, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  useMaxWidth?: boolean;
  classesClose?: string;
  classesContent?: string;
}

export const Modal = ({
  isOpen,
  onClose,
  children,
  useMaxWidth = true,
  classesClose,
  classesContent,
}: PropsWithChildren<ModalProps>) => {
  const xRef = useRef(null);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose} initialFocus={xRef}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-8 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className={twMerge(
                  'p-2 rounded-md bg-white border border-black-100 box-border text-left align-middle shadow-md transition-all overflow-hidden',
                  useMaxWidth && 'w-full',
                )}
              >
                <div className={twMerge('relative', classesContent)}>
                  {children}
                  <div className={twMerge('absolute right-2 top-2', classesClose)}>
                    <FontAwesomeIcon
                      icon={faXmark}
                      className="w-5 h-5 text-black-500 cursor-pointer"
                      onClick={onClose}
                    />
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
